/**
 * Wijzigingen testen — /wijzigingen (okt 2026).
 *
 * Joris: "Door de snelle iteraties mis ik wat er allemaal gedaan is." Per pagina wat er
 * veranderd is, of het op test of live staat, een knop om het te openen, en per gebruiker
 * een teststatus (nog testen / werkt / probleem). Feedback belandt ook in Admin → Feedback.
 *
 * Testpunten (8 okt): "Liefst wil je één testding per onderdeel." Een wijziging heeft een of
 * meer testpunten — titel, stappen, wat je hoort te zien, één Open-knop (live als het daar
 * staat, anders test) — met per punt een eigen ☐ ✓ ✗ en feedback; de wijziging toont
 * "2 van 3 getest". Achter de schermen = één grijze regel, telt niet mee. Oude wijzigingen
 * zonder testpunten test je nog als geheel. Standaard: nog door mij te testen, laatste 14
 * dagen; oudere die nog op test staan liggen ingeklapt onder "Ouder". Beheer (admin) kan een
 * wijziging als niet meer relevant markeren (met ongedaan maken).
 *
 * Afvinken (8 okt): na ✓ schuift het weg (ongedaan maken in de melding); losse ✓-punten
 * klappen in tot "x getest — toon"; ✗ blijft staan tot hij is opgelost. "Toon getest" brengt
 * geteste terug. Joris (admin) zet "Mag naar live" op een wijziging op test → "Klaar voor
 * live ✓"; bovenaan filtert "N klaar voor live". De avondronde leest die lijst via
 * GET /api/v1/wijzigingen/klaar-voor-live; na de gemelde main-merge vervalt de goedkeuring.
 *
 * Bron: de productie-database (intern.fullframe.nl). Op test is de lijst leeg.
 * API: /Api/wijzigingen — main via Node (backend/routes/wijzigingen.js), develop via de
 * engine-voordeur (api/frontdoor/wijzigingen.py). Zelfde paden, zelfde code hier.
 *
 * Rustig ontwerp: één accentkleur, geen kaarten of pillen waar witruimte volstaat,
 * secundaire info (PR's, aantallen) klein en grijs.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Collapse, FormControlLabel, Link, MenuItem, Stack, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { TOOLS } from '../../ui-components/GlobalSearch';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { resolveToolName } from '../../config/navigation';
import { useUserRole } from '../../contexts/UserRoleContext';
import {
  ACHTER_DE_SCHERMEN, LIVE_URL, OMGEVING_TEKST, RECENT_DAGEN, STATUS_TEKST, VERDWIJN_MS, datumTekst,
  filterWijzigingen, groepeerPerPagina, isAchter, klaarVoorLive, liveLink, openLink, paginaLabel, paginaOpties,
  splitsOuder, teTesten, testLink, versieEnDatum, voortgangTekst, werkPuntBij, zichtbarePunten,
} from './wijzigingenLogica';

const KLEUR = {
  tekst: '#1f2937', zacht: '#6b7280', lijn: '#eceef1', accent: '#526075', accentZacht: '#eef2f8',
  probleem: '#b42318',
};

async function vraag(pad, opties = {}) {
  const r = await authFetch(`${BACKEND_URL}${pad}`, {
    ...opties,
    headers: { 'Content-Type': 'application/json', ...(opties.headers || {}) },
    body: opties.body !== undefined ? JSON.stringify(opties.body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : (d.error || `Fout ${r.status}`));
  return d;
}

export const wijzigingenApi = {
  lijst: () => vraag('/Api/wijzigingen'),
  status: (id, status) => vraag(`/Api/wijzigingen/${id}/status`, { method: 'PUT', body: { status } }),
  feedback: (id, body) => vraag(`/Api/wijzigingen/${id}/feedback`, { method: 'POST', body }),
  puntStatus: (id, pid, status) => vraag(`/Api/wijzigingen/${id}/testpunten/${pid}/status`, { method: 'PUT', body: { status } }),
  puntFeedback: (id, pid, body) => vraag(`/Api/wijzigingen/${id}/testpunten/${pid}/feedback`, { method: 'POST', body }),
  relevant: (id, relevant) => vraag(`/Api/wijzigingen/${id}/relevant`, { method: 'PUT', body: { relevant } }),
  liveGoedkeuring: (id, goedgekeurd) => vraag(`/Api/wijzigingen/${id}/live-goedkeuring`, { method: 'PUT', body: { goedgekeurd } }),
};

const labelVan = (route) => paginaLabel(route, resolveToolName, TOOLS);

function OmgevingLabel({ label }) {
  const gepland = label === 'test_live_gepland';
  return (
    <Typography component="span" sx={{
      fontSize: 11.5, fontWeight: 600, letterSpacing: '0.02em', whiteSpace: 'nowrap',
      color: gepland ? KLEUR.accent : KLEUR.zacht,
      bgcolor: gepland ? KLEUR.accentZacht : 'transparent',
      px: gepland ? 0.75 : 0, py: 0.1, borderRadius: 1,
    }}>
      {OMGEVING_TEKST[label] || OMGEVING_TEKST.test}
    </Typography>
  );
}

function StatusKnoppen({ status, bezig, onKies }) {
  return (
    <ToggleButtonGroup
      size="small" exclusive value={status || 'open'} disabled={bezig}
      onChange={(_, v) => { if (v) onKies(v); }}
      sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.25, fontSize: 13, textTransform: 'none', borderColor: KLEUR.lijn, color: KLEUR.zacht } }}
    >
      <ToggleButton value="open" aria-label={STATUS_TEKST.open}><Tooltip title={STATUS_TEKST.open}><span>☐</span></Tooltip></ToggleButton>
      <ToggleButton value="werkt" aria-label={STATUS_TEKST.werkt}
        sx={{ '&.Mui-selected': { color: KLEUR.accent, bgcolor: KLEUR.accentZacht } }}>
        <Tooltip title={STATUS_TEKST.werkt}><span>✓</span></Tooltip>
      </ToggleButton>
      <ToggleButton value="probleem" aria-label={STATUS_TEKST.probleem}
        sx={{ '&.Mui-selected': { color: KLEUR.probleem, bgcolor: '#fdf1f0' } }}>
        <Tooltip title={STATUS_TEKST.probleem}><span>✗</span></Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

/** Feedbackveld. opslaan(tekst) → antwoord van de server; de rest is hier hetzelfde. */
function FeedbackVeld({ probleem, opslaan, onKlaar, onAnnuleer }) {
  const [tekst, setTekst] = useState('');
  const [bezig, setBezig] = useState(false);
  const bewaar = async () => {
    if (!tekst.trim()) { toast.error('Schrijf kort wat je opviel'); return; }
    setBezig(true);
    try {
      const uit = await opslaan(tekst.trim());
      toast.success(`Feedback opgeslagen (#${uit.feedback_id} in Admin → Feedback)`);
      onKlaar(uit, tekst.trim());
    } catch (e) {
      toast.error(e.message);
    } finally { setBezig(false); }
  };
  return (
    <Box sx={{ mt: 1.5, maxWidth: 720 }}>
      <TextField
        autoFocus fullWidth multiline minRows={2} size="small" value={tekst}
        onChange={(e) => setTekst(e.target.value)}
        placeholder={probleem ? 'Wat ging er mis? Waar klikte je, wat zag je?' : 'Wat viel je op?'}
        inputProps={{ 'aria-label': 'Feedback' }}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button size="small" variant="contained" disableElevation onClick={bewaar} disabled={bezig}
          sx={{ textTransform: 'none', bgcolor: KLEUR.accent }}>
          {bezig ? 'Bezig…' : 'Opslaan'}
        </Button>
        <Button size="small" onClick={onAnnuleer} sx={{ textTransform: 'none', color: KLEUR.zacht }}>Annuleren</Button>
      </Stack>
    </Box>
  );
}

function TestpuntRegel({ w, punt, nummer, groepsRoute, weg, onPuntStatus, onPuntFeedback }) {
  const [veld, setVeld] = useState(null); // null | 'feedback' | 'probleem'
  const [bezig, setBezig] = useState(false);
  const route = punt.route || groepsRoute || (w.routes || [])[0] || null;
  const kies = async (status) => {
    setBezig(true);
    try { await onPuntStatus(w, punt, status); } finally { setBezig(false); }
    if (status === 'probleem') setVeld('probleem');
  };
  return (
    <Collapse in={!weg} timeout={VERDWIJN_MS}>
    <Box data-testpunt={punt.id} sx={{ py: 1.25, pl: { xs: 0, md: 2 }, borderTop: `1px dashed ${KLEUR.lijn}` }}>
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: KLEUR.tekst }}>
            <Box component="span" sx={{ color: KLEUR.zacht, fontWeight: 500, mr: 1 }}>{nummer}.</Box>
            {punt.titel}
          </Typography>
          {punt.stappen && (
            <Typography sx={{ fontSize: 13.5, color: KLEUR.tekst, mt: 0.25 }}>{punt.stappen}</Typography>
          )}
          {punt.verwacht && (
            <Typography sx={{ fontSize: 13.5, color: KLEUR.zacht, mt: 0.25 }}>Verwacht: {punt.verwacht}</Typography>
          )}
          {punt.mijn_opmerking && !veld && (
            <Typography sx={{ fontSize: 12.5, color: KLEUR.zacht, mt: 0.5, fontStyle: 'italic' }}>
              Jouw feedback: {punt.mijn_opmerking}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}>
          {route && (
            <Button size="small" href={openLink(route, punt.omgeving)} target="_blank" rel="noreferrer"
              title={punt.omgeving === 'live' ? 'Opent op intern.fullframe.nl' : 'Opent op test.fullframe.nl'}
              sx={{ textTransform: 'none', color: KLEUR.accent }}>Open</Button>
          )}
          <StatusKnoppen status={punt.mijn_status} bezig={bezig} onKies={kies} />
          <Button size="small" onClick={() => setVeld(veld ? null : 'feedback')}
            sx={{ textTransform: 'none', color: KLEUR.zacht, minWidth: 0 }}>Feedback</Button>
        </Stack>
      </Box>
      {veld && (
        <FeedbackVeld
          probleem={veld === 'probleem' || punt.mijn_status === 'probleem'}
          opslaan={(tekst) => wijzigingenApi.puntFeedback(w.id, punt.id, {
            opmerking: tekst, probleem: veld === 'probleem' || punt.mijn_status === 'probleem',
            pagina: route ? (labelVan(route) || undefined) : undefined,
          })}
          onAnnuleer={() => setVeld(null)}
          onKlaar={(uit, tekst) => { setVeld(null); onPuntFeedback(w, punt, uit, tekst); }}
        />
      )}
    </Box>
    </Collapse>
  );
}

function KlaarVoorLiveChip() {
  return (
    <Typography component="span" sx={{
      fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', color: '#1f6f43', bgcolor: '#e9f6ee', px: 0.75, py: 0.1, borderRadius: 1,
    }}>
      Klaar voor live ✓
    </Typography>
  );
}

function WijzigingRegel({ w, route, uitgelicht, isAdmin, toonGetest, animerend, onStatus, onFeedbackOpgeslagen,
  onPuntStatus, onPuntFeedback, onRelevant, onLiveGoedkeuring }) {
  const [veld, setVeld] = useState(null); // null | 'feedback' | 'probleem'
  const [bezig, setBezig] = useState(false);
  const [toonPunten, setToonPunten] = useState(false);
  const { versie, datum } = versieEnDatum(w);
  const meta = [versie ? `v${versie}` : null, datumTekst(datum)].filter(Boolean).join(' · ');
  const punten = w.testpunten || [];
  const achter = isAchter(w);
  const geheel = !punten.length && !achter;   // oude wijziging: als geheel testen
  const zichtbaar = new Set(zichtbarePunten(w, { toonGetest: toonGetest || toonPunten, animerend }).map((p) => p.id));
  const ingeklapt = punten.filter((p) => !zichtbaar.has(p.id)).length;
  const toonGetestKnop = punten.length > 0 && (ingeklapt > 0 || (toonPunten && !toonGetest));

  const kies = async (status) => {
    setBezig(true);
    try { await onStatus(w, status); } finally { setBezig(false); }
    if (status === 'probleem') setVeld('probleem');
  };

  return (
    <Collapse in={!animerend.has(`w-${w.id}`)} timeout={VERDWIJN_MS}>
    <Box id={`w-${w.id}`} sx={{
      py: 2, borderTop: `1px solid ${KLEUR.lijn}`, opacity: w.niet_relevant ? 0.6 : 1,
      bgcolor: uitgelicht ? KLEUR.accentZacht : 'transparent', px: uitgelicht ? 1.5 : 0, borderRadius: uitgelicht ? 1 : 0,
    }}>
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: KLEUR.tekst }}>{w.titel}</Typography>
            <OmgevingLabel label={w.omgevings_label} />
            {w.live_goedgekeurd && <KlaarVoorLiveChip />}
            {w.voortgang && (
              <Typography component="span" sx={{ fontSize: 12.5, color: w.mijn_status === 'probleem' ? KLEUR.probleem : KLEUR.zacht }}>
                {voortgangTekst(w.voortgang)}
              </Typography>
            )}
            {w.niet_relevant && (
              <Typography component="span" sx={{ fontSize: 12.5, color: KLEUR.zacht }}>niet meer relevant</Typography>
            )}
          </Stack>
          {w.beschrijving && (
            <Typography sx={{ fontSize: 13.5, color: punten.length ? KLEUR.zacht : KLEUR.tekst, mt: 0.5, whiteSpace: 'pre-line' }}>
              {w.beschrijving}
            </Typography>
          )}
          {geheel && w.test_instructie && (
            <Typography sx={{ fontSize: 13.5, color: KLEUR.zacht, mt: 0.5, whiteSpace: 'pre-line' }}>
              Testen: {w.test_instructie}
            </Typography>
          )}
          {achter && (
            <Typography sx={{ fontSize: 13, color: KLEUR.zacht, mt: 0.5 }}>{ACHTER_DE_SCHERMEN} — niets om te testen</Typography>
          )}
          <Typography component="div" sx={{ fontSize: 12, color: KLEUR.zacht, mt: 0.75 }}>
            {meta}
            {(w.prs || []).map((p) => (
              <React.Fragment key={p.nummer}>
                {' · '}
                <Link href={p.url || `https://github.com/Full-Frame/ff-interne-app/pull/${p.nummer}`} target="_blank"
                  rel="noreferrer" underline="hover" sx={{ color: KLEUR.zacht }}>#{p.nummer}</Link>
              </React.Fragment>
            ))}
            {geheel && w.routes?.length > 1 && ` · ook ${w.routes.filter((r) => r !== route).map((r) => labelVan(r)).join(', ')}`}
            {geheel && (w.getest?.werkt || w.getest?.probleem) ? ` · getest: ${w.getest.werkt} werkt${w.getest.probleem ? `, ${w.getest.probleem} probleem` : ''}` : ''}
            {isAdmin && (
              <>
                {' · '}
                <Link component="button" underline="hover" onClick={() => onRelevant(w, !!w.niet_relevant)}
                  sx={{ fontSize: 12, color: KLEUR.zacht, verticalAlign: 'baseline' }}>
                  {w.niet_relevant ? 'Toch weer relevant' : 'Markeer als niet meer relevant'}
                </Link>
              </>
            )}
            {isAdmin && w.omgeving !== 'live' && !w.niet_relevant && (
              <>
                {' · '}
                <Link component="button" underline="hover" onClick={() => onLiveGoedkeuring(w, !w.live_goedgekeurd)}
                  sx={{ fontSize: 12, color: w.live_goedgekeurd ? KLEUR.zacht : KLEUR.accent, fontWeight: 600, verticalAlign: 'baseline' }}>
                  {w.live_goedgekeurd ? 'Toch niet naar live' : 'Mag naar live'}
                </Link>
              </>
            )}
          </Typography>
          {geheel && w.mijn_opmerking && !veld && (
            <Typography sx={{ fontSize: 12.5, color: KLEUR.zacht, mt: 0.5, fontStyle: 'italic' }}>
              Jouw feedback: {w.mijn_opmerking}
            </Typography>
          )}
        </Box>
        {geheel && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}>
            {route && (
              <Button size="small" href={testLink(route)} target="_blank" rel="noreferrer"
                sx={{ textTransform: 'none', color: KLEUR.accent }}>Open op test</Button>
            )}
            {route && w.omgeving === 'live' && (
              <Button size="small" href={liveLink(route)} target="_blank" rel="noreferrer"
                sx={{ textTransform: 'none', color: KLEUR.accent }}>Open live</Button>
            )}
            <StatusKnoppen status={w.mijn_status} bezig={bezig} onKies={kies} />
            <Button size="small" onClick={() => setVeld(veld ? null : 'feedback')}
              sx={{ textTransform: 'none', color: KLEUR.zacht, minWidth: 0 }}>Feedback</Button>
          </Stack>
        )}
      </Box>
      {geheel && veld && (
        <FeedbackVeld
          probleem={veld === 'probleem' || w.mijn_status === 'probleem'}
          opslaan={(tekst) => wijzigingenApi.feedback(w.id, {
            opmerking: tekst, probleem: veld === 'probleem' || w.mijn_status === 'probleem',
            pagina: route ? (labelVan(route) || undefined) : undefined, route: route || undefined,
          })}
          onAnnuleer={() => setVeld(null)}
          onKlaar={(uit, tekst) => { setVeld(null); onFeedbackOpgeslagen(w, uit, tekst); }}
        />
      )}
      {punten.length > 0 && (
        <Box sx={{ mt: 1.25 }}>
          {punten.map((p, i) => (zichtbaar.has(p.id) ? (
            <TestpuntRegel key={p.id} w={w} punt={p} nummer={i + 1} groepsRoute={route}
              weg={animerend.has(`p-${p.id}`)} onPuntStatus={onPuntStatus} onPuntFeedback={onPuntFeedback} />
          ) : null))}
          {toonGetestKnop && (
            <Link component="button" underline="hover" onClick={() => setToonPunten((t) => !t)}
              sx={{ fontSize: 12.5, color: KLEUR.zacht, mt: 0.75, display: 'inline-block' }}>
              {ingeklapt > 0 ? `✓ ${ingeklapt} getest — toon` : 'Verberg geteste punten'}
            </Link>
          )}
        </Box>
      )}
    </Box>
    </Collapse>
  );
}

function Groepen({ groepen, gevraagd, regelProps }) {
  return (
    <Stack spacing={5}>
      {groepen.map((g) => (
        <Box key={g.sleutel} component="section" aria-label={g.label}>
          <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700, color: KLEUR.tekst, mb: 0.5, fontFamily: '"Manrope", system-ui, sans-serif' }}>
            {g.label}
          </Typography>
          {g.items.map((w) => (
            <WijzigingRegel key={w.id} w={w} route={g.route} uitgelicht={w.id === gevraagd} {...regelProps} />
          ))}
        </Box>
      ))}
    </Stack>
  );
}

const STANDAARD_FILTERS = {
  omgeving: 'alles', alleenTeTesten: true, laatste14: true, toonGetest: false, klaarVoorLive: false, pagina: 'alle',
};

export default function Wijzigingen() {
  const location = useLocation();
  const { role } = useUserRole();
  const isAdmin = role === 'admin';
  const gevraagd = useMemo(() => Number(new URLSearchParams(location.search).get('w')) || null, [location.search]);
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [filters, setFilters] = useState(() => (gevraagd
    ? { ...STANDAARD_FILTERS, alleenTeTesten: false, laatste14: false, toonGetest: true } : STANDAARD_FILTERS));
  const [ouderOpen, setOuderOpen] = useState(false);
  const [vastgehouden, setVastgehouden] = useState(() => new Set());
  const [animerend, setAnimerend] = useState(() => new Set());   // 'w-<id>' / 'p-<id>' die wegschuiven
  const gescrold = useRef(false);

  useEffect(() => registreerOngedaanSneltoets(), []);

  const laad = useCallback(async () => {
    try {
      setFout(null);
      setData(await wijzigingenApi.lijst());
      setVastgehouden(new Set());
    } catch (e) { setFout(e.message); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  useEffect(() => {
    if (!gevraagd || !data || gescrold.current) return;
    const el = document.getElementById(`w-${gevraagd}`);
    if (el?.scrollIntoView) { el.scrollIntoView({ block: 'center' }); gescrold.current = true; }
  }, [gevraagd, data]);

  const zetLokaal = useCallback((id, wijzig) => {
    setData((d) => (d ? {
      ...d,
      wijzigingen: d.wijzigingen.map((w) => (w.id === id ? (typeof wijzig === 'function' ? wijzig(w) : { ...w, ...wijzig }) : w)),
    } : d));
  }, []);
  const houdVast = (id) => setVastgehouden((s) => new Set(s).add(id));

  // Na ✓: kort wegschuiven, daarna laat het filter hem los (ongedaan maken brengt hem terug)
  const schuifWeg = useCallback((sleutel, wijzigingId) => {
    setAnimerend((s) => new Set(s).add(sleutel));
    setTimeout(() => {
      setAnimerend((s) => { const n = new Set(s); n.delete(sleutel); return n; });
      if (wijzigingId != null) setVastgehouden((s) => { const n = new Set(s); n.delete(wijzigingId); return n; });
    }, VERDWIJN_MS);
  }, []);

  const opStatus = useCallback(async (w, status) => {
    const vorige = w.mijn_status || 'open';
    if (status === vorige) return;
    houdVast(w.id);
    zetLokaal(w.id, { mijn_status: status });
    try {
      await wijzigingenApi.status(w.id, status);
    } catch (e) {
      zetLokaal(w.id, { mijn_status: vorige });
      toast.error(`Niet opgeslagen: ${e.message}`);
      return;
    }
    if (status === 'werkt') schuifWeg(`w-${w.id}`, w.id);
    biedOngedaanAan(`"${w.titel}": ${STATUS_TEKST[status].toLowerCase()}`, async () => {
      await wijzigingenApi.status(w.id, vorige);
      zetLokaal(w.id, { mijn_status: vorige });
    });
  }, [zetLokaal, schuifWeg]);

  const opFeedback = useCallback((w, uit, tekst) => {
    houdVast(w.id);
    zetLokaal(w.id, { mijn_status: uit.status, mijn_opmerking: tekst, mijn_feedback_id: uit.feedback_id });
  }, [zetLokaal]);

  const opPuntStatus = useCallback(async (w, punt, status) => {
    const vorige = punt.mijn_status || 'open';
    if (status === vorige) return;
    houdVast(w.id);
    // Het punt schuift meteen (samen met de optimistische status) weg, anders flitst het
    // eerst weg en daarna terug in de animatie
    if (status === 'werkt') schuifWeg(`p-${punt.id}`);
    zetLokaal(w.id, (x) => werkPuntBij(x, punt.id, { mijn_status: status }));
    try {
      await wijzigingenApi.puntStatus(w.id, punt.id, status);
    } catch (e) {
      zetLokaal(w.id, (x) => werkPuntBij(x, punt.id, { mijn_status: vorige }));
      toast.error(`Niet opgeslagen: ${e.message}`);
      return;
    }
    // laatste punt ✓ → de hele wijziging schuift weg
    if (status === 'werkt' && werkPuntBij(w, punt.id, { mijn_status: 'werkt' }).mijn_status === 'werkt') {
      schuifWeg(`w-${w.id}`, w.id);
    }
    biedOngedaanAan(`"${punt.titel}": ${STATUS_TEKST[status].toLowerCase()}`, async () => {
      await wijzigingenApi.puntStatus(w.id, punt.id, vorige);
      zetLokaal(w.id, (x) => werkPuntBij(x, punt.id, { mijn_status: vorige }));
    });
  }, [zetLokaal, schuifWeg]);

  const opPuntFeedback = useCallback((w, punt, uit, tekst) => {
    houdVast(w.id);
    zetLokaal(w.id, (x) => werkPuntBij(x, punt.id, { mijn_status: uit.status, mijn_opmerking: tekst }));
  }, [zetLokaal]);

  // "Markeer als niet meer relevant": direct, met ongedaan maken (vangnet; zie utils/ongedaan)
  const opRelevant = useCallback(async (w, relevant) => {
    houdVast(w.id);
    zetLokaal(w.id, { niet_relevant: !relevant });
    try {
      await wijzigingenApi.relevant(w.id, relevant);
    } catch (e) {
      zetLokaal(w.id, { niet_relevant: relevant });
      toast.error(`Niet opgeslagen: ${e.message}`);
      return;
    }
    biedOngedaanAan(relevant ? `"${w.titel}" is weer relevant` : `"${w.titel}" gemarkeerd als niet meer relevant`, async () => {
      await wijzigingenApi.relevant(w.id, !relevant);
      zetLokaal(w.id, { niet_relevant: relevant });
    }, { eigenKnop: true });
  }, [zetLokaal]);

  // "Mag naar live" (admin): direct, met ongedaan maken
  const opLiveGoedkeuring = useCallback(async (w, goedgekeurd) => {
    houdVast(w.id);
    zetLokaal(w.id, { live_goedgekeurd: goedgekeurd });
    try {
      await wijzigingenApi.liveGoedkeuring(w.id, goedgekeurd);
    } catch (e) {
      zetLokaal(w.id, { live_goedgekeurd: !goedgekeurd });
      toast.error(`Niet opgeslagen: ${e.message}`);
      return;
    }
    biedOngedaanAan(goedgekeurd ? `"${w.titel}" mag naar live` : `"${w.titel}" gaat toch niet naar live`, async () => {
      await wijzigingenApi.liveGoedkeuring(w.id, !goedgekeurd);
      zetLokaal(w.id, { live_goedgekeurd: !goedgekeurd });
    }, { eigenKnop: true });
  }, [zetLokaal]);

  const lijst = useMemo(() => data?.wijzigingen || [], [data]);
  const zichtbaar = useMemo(() => filterWijzigingen(lijst, filters, vastgehouden), [lijst, filters, vastgehouden]);
  const { hoofd, ouder } = useMemo(() => splitsOuder(zichtbaar, { laatste14: filters.laatste14 && !filters.klaarVoorLive },
    new Date(), vastgehouden), [zichtbaar, filters, vastgehouden]);
  const aantalKlaar = klaarVoorLive(lijst).length;
  const groepen = useMemo(() => groepeerPerPagina(hoofd, labelVan, filters.pagina), [hoofd, filters.pagina]);
  const ouderGroepen = useMemo(() => groepeerPerPagina(ouder, labelVan, filters.pagina), [ouder, filters.pagina]);
  const opties = useMemo(() => paginaOpties(lijst, labelVan), [lijst]);
  const aantalTeTesten = lijst.filter(teTesten).length;

  const zet = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const regelProps = {
    isAdmin, toonGetest: filters.toonGetest, animerend, onStatus: opStatus, onFeedbackOpgeslagen: opFeedback,
    onPuntStatus: opPuntStatus, onPuntFeedback: opPuntFeedback, onRelevant: opRelevant, onLiveGoedkeuring: opLiveGoedkeuring,
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="fact_check"
        title="Wijzigingen testen"
        subtitle="Wat er veranderd is, waar je het test, en of jij het al gedaan hebt"
      />

      <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1.5 }}>
        {(aantalKlaar > 0 || filters.klaarVoorLive) && (
          <Button size="small" onClick={() => zet('klaarVoorLive')(!filters.klaarVoorLive)} aria-pressed={filters.klaarVoorLive}
            sx={{
              textTransform: 'none', fontSize: 13.5, fontWeight: 600, color: '#1f6f43',
              bgcolor: filters.klaarVoorLive ? '#d7efe0' : '#e9f6ee', '&:hover': { bgcolor: '#d7efe0' },
            }}>
            {aantalKlaar} klaar voor live
          </Button>
        )}
        <ToggleButtonGroup size="small" exclusive value={filters.omgeving}
          onChange={(_, v) => { if (v) zet('omgeving')(v); }}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.25, fontSize: 13 } }}>
          <ToggleButton value="alles">Alles</ToggleButton>
          <ToggleButton value="test">Alleen test</ToggleButton>
          <ToggleButton value="live">Live</ToggleButton>
        </ToggleButtonGroup>
        <FormControlLabel
          control={<Switch size="small" checked={filters.alleenTeTesten} onChange={(e) => zet('alleenTeTesten')(e.target.checked)}
            inputProps={{ 'aria-label': 'Nog door mij te testen' }} />}
          label={<Typography sx={{ fontSize: 13.5 }}>Nog door mij te testen{data ? ` (${aantalTeTesten})` : ''}</Typography>}
        />
        <FormControlLabel
          control={<Switch size="small" checked={filters.laatste14} onChange={(e) => zet('laatste14')(e.target.checked)}
            inputProps={{ 'aria-label': `Laatste ${RECENT_DAGEN} dagen` }} />}
          label={<Typography sx={{ fontSize: 13.5 }}>Laatste {RECENT_DAGEN} dagen</Typography>}
        />
        <FormControlLabel
          control={<Switch size="small" checked={filters.toonGetest} onChange={(e) => zet('toonGetest')(e.target.checked)}
            inputProps={{ 'aria-label': 'Toon getest' }} />}
          label={<Typography sx={{ fontSize: 13.5, color: KLEUR.zacht }}>Toon getest</Typography>}
        />
        <TextField select size="small" value={filters.pagina} onChange={(e) => zet('pagina')(e.target.value)}
          sx={{ minWidth: 200 }} inputProps={{ 'aria-label': 'Pagina' }}>
          <MenuItem value="alle">Alle pagina's</MenuItem>
          {opties.map((o) => <MenuItem key={o.sleutel} value={o.sleutel}>{o.label}</MenuItem>)}
        </TextField>
      </Stack>

      {!data && !fout && <CircularProgress size={22} />}
      {fout && (
        <Typography sx={{ color: KLEUR.probleem, fontSize: 14 }}>
          Kon de wijzigingen niet laden: {fout}{' '}
          <Link component="button" onClick={laad} sx={{ fontSize: 14 }}>Opnieuw</Link>
        </Typography>
      )}

      {data && lijst.length === 0 && (
        <Typography sx={{ color: KLEUR.zacht, fontSize: 14 }}>
          {data.omgeving === 'test'
            ? <>Dit overzicht wordt bijgehouden op productie: <Link href={`${LIVE_URL}/wijzigingen`}>intern.fullframe.nl/wijzigingen</Link>.</>
            : 'Er zijn nog geen wijzigingen gemeld.'}
        </Typography>
      )}
      {data && lijst.length > 0 && hoofd.length === 0 && (
        <Typography sx={{ color: KLEUR.zacht, fontSize: 14, mb: 3 }}>
          {filters.alleenTeTesten ? 'Je hebt alles van de laatste tijd getest. ' : 'Niets met deze filters. '}
          <Link component="button" onClick={() => setFilters({ ...STANDAARD_FILTERS, alleenTeTesten: false, laatste14: false, toonGetest: true })}
            sx={{ fontSize: 14 }}>Toon alles</Link>
        </Typography>
      )}

      <Groepen groepen={groepen} gevraagd={gevraagd} regelProps={regelProps} />

      {ouder.length > 0 && (
        <Box component="section" aria-label="Ouder" sx={{ mt: 6 }}>
          <Button size="small" onClick={() => setOuderOpen((o) => !o)} aria-expanded={ouderOpen}
            sx={{ textTransform: 'none', color: KLEUR.zacht, px: 0, fontSize: 14 }}>
            {ouderOpen ? '▾' : '▸'} Ouder ({ouder.length}) — langer dan {RECENT_DAGEN} dagen op test, niemand mee bezig
          </Button>
          {ouderOpen && (
            <Box sx={{ mt: 2 }}>
              <Groepen groepen={ouderGroepen} gevraagd={gevraagd} regelProps={regelProps} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
